/**
 * Resume positions — server is source of truth (cross-device sync).
 *
 * Same handoff pattern as favorites.ts: server holds the canonical list,
 * Dexie is a write-through cache so the player can pick a starting
 * position without blocking on a network call. On every successful POST
 * the local row is updated to keep them in lock-step.
 *
 * ## Throttling
 * `<video>` fires `timeupdate` ~4 Hz; we'd hammer the server. The
 * client-side rule is: at most one POST per ~10 s per (kind, stream_id),
 * coalesced via a per-stream timer. The last in-flight value is captured
 * in a sentinel — when the tab is closing, `flushPendingResume()` blasts
 * it out via `navigator.sendBeacon` so the user doesn't lose the most
 * recent few seconds of progress.
 *
 * ## Cold launch
 * `Resume.local()` reads the Dexie cache synchronously; the player
 * applies it and starts. `syncResumeCache()` reconciles in the
 * background. If the server has a *later* position than the cache, the
 * player silently fast-forwards (we never rewind on cold launch).
 *
 * ## Delete-on-finish
 * Client `DELETE`s the row when the user crosses 95 % of duration (or
 * the existing finished-watching action). Server doesn't auto-cleanup —
 * "rewatching from start" is achieved with a fresh POST(position_ms=0).
 *
 * ## Catchup metadata
 * For `stream_kind === 'catchup'` the row carries five extra fields so
 * the Hub continue-watching click can re-resolve the timeshift URL via
 * `/api/playlists/{pid}/catchup/resolve`. The server enforces these as
 * required for catchup; ignored for every other kind.
 */
import { api } from "./api";
import { db, itemKey, type ResumeRow } from "./db";

export type ResumeStreamKind = "live" | "vod" | "series" | "catchup";

export interface ServerResume {
  id: number;
  stream_kind: ResumeStreamKind;
  stream_id: string;
  title: string;
  poster: string | null;
  position_ms: number;
  duration_ms: number;
  series_id: string | null;
  catchup_channel_id: string | null;
  catchup_start_ms: number | null;
  catchup_duration_min: number | null;
  catchup_channel_name: string | null;
  catchup_programme_title: string | null;
  updated_at: string;
}

interface ListResp { items: ServerResume[] }

export interface ResumeUpsertBody {
  stream_kind: ResumeStreamKind;
  stream_id: string;
  title: string;
  poster?: string | null;
  position_ms: number;
  duration_ms: number;
  series_id?: string | null;
  catchup_channel_id?: string | null;
  catchup_start_ms?: number | null;
  catchup_duration_min?: number | null;
  catchup_channel_name?: string | null;
  catchup_programme_title?: string | null;
}

export const Resume = {
  list: (pid: number) =>
    api.get<ListResp>(`/api/playlists/${pid}/resume`).then((r) => r.data.items),

  upsert: (pid: number, body: ResumeUpsertBody) =>
    api.post<ServerResume>(`/api/playlists/${pid}/resume`, body).then((r) => r.data),

  remove: (pid: number, kind: ResumeStreamKind, streamId: string) =>
    api.delete<void>(
      `/api/playlists/${pid}/resume/${kind}/${encodeURIComponent(streamId)}`
    ),

  /** Series-level wipe — clears every episode-row for one series in one
   * round-trip. Used by the "Mark series as watched" action on series
   * detail. The server walks `series_id` and deletes all matching rows. */
  removeSeries: (pid: number, seriesId: string) =>
    api.delete<void>(
      `/api/playlists/${pid}/resume/series/${encodeURIComponent(seriesId)}`
    ),
};

/** Replace the local Dexie cache with the server's view for one playlist. */
export async function syncResumeCache(
  userId: number,
  playlistId: number,
  serverItems: ServerResume[],
): Promise<void> {
  await db.resume.where("playlistId").equals(playlistId).delete();
  if (!serverItems.length) return;
  const rows: ResumeRow[] = serverItems.map((s) => ({
    key: itemKey(userId, playlistId, s.stream_kind, s.stream_id),
    userId,
    playlistId,
    streamKind: s.stream_kind,
    streamId: s.stream_id,
    title: s.title,
    poster: s.poster,
    position: s.position_ms / 1000,
    duration: s.duration_ms / 1000,
    seriesId: s.series_id,
    catchupChannelId: s.catchup_channel_id,
    catchupStartMs: s.catchup_start_ms,
    catchupDurationMin: s.catchup_duration_min,
    catchupChannelName: s.catchup_channel_name,
    catchupProgrammeTitle: s.catchup_programme_title,
    updatedAt: Date.parse(s.updated_at) || Date.now(),
  }));
  await db.resume.bulkPut(rows);
}

// ─── Throttled upsert ───────────────────────────────────────────────────
// Rule: at most one POST per stream per `RESUME_DEBOUNCE_MS`. The latest
// value submitted via `scheduleResumeUpsert()` is stored; when the timer
// elapses (or `flushPendingResume()` is called on tab close) the latest
// is sent. Earlier intermediate values are dropped — the carousel only
// shows the freshest position anyway.
const RESUME_DEBOUNCE_MS = 10_000;

interface PendingEntry {
  pid: number;
  body: ResumeUpsertBody;
  timer: number | null;
}

const pending = new Map<string, PendingEntry>();

function pendingKey(pid: number, kind: ResumeStreamKind, streamId: string) {
  return `${pid}:${kind}:${streamId}`;
}

async function flushOne(key: string): Promise<void> {
  const entry = pending.get(key);
  if (!entry) return;
  pending.delete(key);
  if (entry.timer !== null) clearTimeout(entry.timer);
  try {
    await Resume.upsert(entry.pid, entry.body);
    // Note: WatchPage already mirrors the position into Dexie locally on
    // every onProgress tick, so no need to round-trip here.
  } catch {
    /* best-effort — local cache already has the value */
  }
}

/** Schedule a debounced upsert. Repeated calls within
 * `RESUME_DEBOUNCE_MS` for the same (pid, kind, stream_id) coalesce
 * into a single POST carrying the most recent values. */
export function scheduleResumeUpsert(pid: number, body: ResumeUpsertBody): void {
  const key = pendingKey(pid, body.stream_kind, body.stream_id);
  const existing = pending.get(key);
  if (existing) {
    existing.body = body;
    return;
  }
  const timer = window.setTimeout(() => { void flushOne(key); }, RESUME_DEBOUNCE_MS);
  pending.set(key, { pid, body, timer });
}

/** Best-effort flush of every pending upsert via `navigator.sendBeacon`.
 * Called from `pagehide` / `visibilitychange` so the user's last few
 * seconds of progress survive a tab close. Beacon requests are
 * unconditionally sent; the server treats them like any other POST. */
export function flushPendingResume(): void {
  if (typeof navigator === "undefined" || !pending.size) return;
  const beacon = navigator.sendBeacon?.bind(navigator);
  for (const [key, entry] of pending) {
    if (entry.timer !== null) clearTimeout(entry.timer);
    pending.delete(key);
    const url = `/api/playlists/${entry.pid}/resume`;
    if (beacon) {
      try {
        const blob = new Blob([JSON.stringify(entry.body)], { type: "application/json" });
        beacon(url, blob);
      } catch {
        /* ignore */
      }
    } else {
      // No sendBeacon (very old browser): fire-and-forget fetch with keepalive.
      try {
        void fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(entry.body),
          credentials: "include",
          keepalive: true,
        });
      } catch {
        /* ignore */
      }
    }
  }
}

let _pageHideWired = false;
/** Wire pagehide+visibilitychange flush. Idempotent — safe to call from
 * any top-level effect; subsequent calls are no-ops. */
export function ensurePageHideFlush(): void {
  if (_pageHideWired || typeof window === "undefined") return;
  _pageHideWired = true;
  const handler = () => flushPendingResume();
  window.addEventListener("pagehide", handler);
  window.addEventListener("beforeunload", handler);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") handler();
  });
}
